/**
 * BRXFlowRuntime
 *
 * Detects the active Embeddables flow root, attaches a page-change observer,
 * and runs enhancement rules on mount and every page transition.
 *
 * Usage:
 *   var rt = window.BRXFlowRuntime.start({
 *     enhancements:   ctx.enhancements,   // array from BRXFlowEnhancements
 *     activeFlowRef:  ctx.activeFlowRef,   // { current } kept in sync here
 *     revealedRef:    { value: false },    // set true by entry controller on reveal
 *     onFlowDetected: function(flowEl) {}  // called once on first detection
 *   });
 *   rt.stop(); // disconnects all observers
 */
(function () {
    var DEBUG = false;
    function debugLog() {
        if (!DEBUG || !window.console || !console.warn) return;
        console.warn.apply(console, arguments);
    }

    function getFlowPageId(flowRoot) {
        return flowRoot ? (flowRoot.getAttribute('data-flow-page-id-value') || '') : '';
    }

    function getFlowPageKey(flowRoot) {
        if (!flowRoot) return '';
        var pageEl = flowRoot.querySelector('[data-controller~="page"]');
        return pageEl ? (pageEl.getAttribute('data-page-page-key-value') || '') : '';
    }

    // Intentional global lookup: Embeddables mounts the live flow asynchronously,
    // and wrapper-scoped detection was unreliable in testing. Assumes a single
    // active flow on the page.
    function findFlowRoot() {
        var flowRoots = document.querySelectorAll('[data-controller="flow"]');
        if (flowRoots.length > 1) {
            debugLog('[BRXFlowRuntime] Multiple flow roots detected; using the first one.', flowRoots);
        }
        return flowRoots[0] || null;
    }

    function start(options) {
        var enhancements   = (options && options.enhancements)   || [];
        var activeFlowRef  = (options && options.activeFlowRef)  || { current: null };
        var revealedRef    = (options && options.revealedRef)    || { value: false };
        var onFlowDetected = (options && options.onFlowDetected) || function () {};

        var pageObserver      = null;
        var bootstrapObserver = null;
        var lastSeenPageId    = null;
        var lastSeenPageKey   = null;

        function runEnhancements(flowRoot, meta) {
            if (!flowRoot) return;
            var pageId      = getFlowPageId(flowRoot);
            var pageKey     = getFlowPageKey(flowRoot);
            var reason      = (meta && meta.reason) || 'mount';
            var prevPageId  = (meta && meta.previousPageId  != null) ? meta.previousPageId  : lastSeenPageId;
            var prevPageKey = (meta && meta.previousPageKey != null) ? meta.previousPageKey : lastSeenPageKey;

            var ctx = {
                flowRoot: flowRoot,
                pageId: pageId,
                pageKey: pageKey,
                reason: reason,
                previousPageId: prevPageId,
                previousPageKey: prevPageKey
            };

            for (var i = 0; i < enhancements.length; i++) {
                try {
                    if (enhancements[i].when(ctx)) enhancements[i].run(ctx);
                } catch (e) {
                    debugLog('[BRXFlowRuntime] Enhancement failed:', enhancements[i].key, e);
                }
            }

            lastSeenPageId  = pageId;
            lastSeenPageKey = pageKey;
        }

        function attachPageObserver(flowEl) {
            if (!flowEl) return;
            if (pageObserver) { pageObserver.disconnect(); pageObserver = null; }

            var lastKnownPageId  = getFlowPageId(flowEl);
            var lastKnownPageKey = getFlowPageKey(flowEl);
            var lastScrollAt = 0;

            pageObserver = new MutationObserver(function (mutations) {
                for (var i = 0; i < mutations.length; i++) {
                    var m = mutations[i];
                    if (m.type !== 'attributes') continue;
                    if (m.attributeName !== 'data-flow-page-id-value') continue;

                    var nextPageId = flowEl.getAttribute('data-flow-page-id-value') || '';
                    if (!nextPageId || nextPageId === lastKnownPageId) continue;

                    var prevId  = lastKnownPageId;
                    var prevKey = lastKnownPageKey;
                    lastKnownPageId  = nextPageId;
                    lastKnownPageKey = getFlowPageKey(flowEl);

                    // Don't scroll before the overlay has been revealed
                    if (!revealedRef.value) return;

                    var now = Date.now();
                    if (now - lastScrollAt < 250) return;
                    lastScrollAt = now;

                    requestAnimationFrame(function () {
                        runEnhancements(flowEl, {
                            reason: 'page_change',
                            previousPageId: prevId,
                            previousPageKey: prevKey
                        });
                        var scroller = document.scrollingElement || document.documentElement;
                        scroller.scrollTo({ top: 0, behavior: 'smooth' });
                    });
                    return;
                }
            });

            pageObserver.observe(flowEl, {
                attributes: true,
                attributeFilter: ['data-flow-page-id-value']
            });
        }

        function onFlowFound(flowEl) {
            activeFlowRef.current = flowEl;
            attachPageObserver(flowEl);
            runEnhancements(flowEl, { reason: 'mount' });
            onFlowDetected(flowEl);
        }

        var existingFlow = findFlowRoot();
        if (existingFlow) {
            onFlowFound(existingFlow);
        } else {
            bootstrapObserver = new MutationObserver(function () {
                var flowEl = findFlowRoot();
                if (!flowEl) return;
                bootstrapObserver.disconnect();
                bootstrapObserver = null;
                onFlowFound(flowEl);
            });
            bootstrapObserver.observe(document.body, { childList: true, subtree: true });
        }

        function stop() {
            if (pageObserver)      { pageObserver.disconnect();      pageObserver = null; }
            if (bootstrapObserver) { bootstrapObserver.disconnect(); bootstrapObserver = null; }
        }

        return { stop: stop };
    }

    window.BRXFlowRuntime = { start: start };
})();
